'use client';

import type { CSSProperties } from 'react';
import { useId, useRef } from 'react';
import { useElementWidth } from '../core/useElementWidth';
import styles from './IrregularSample.module.css';

export type Sample = { t: number; value: number };

const TICK_OFFSET = 3;
const TICK_LENGTH = 8;
const TICK_BAND = TICK_OFFSET + TICK_LENGTH;

// d3's curveBumpX: cubic segments with control points at the horizontal
// midpoint, so the line eases between readings without overshooting.
const bumpX = (pts: [number, number][]) => {
  if (pts.length === 0) return '';
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const mx = (x0 + x1) / 2;
    d += ` C ${mx} ${y0}, ${mx} ${y1}, ${x1} ${y1}`;
  }
  return d;
};

export const IrregularSample = ({
  samples,
  extrapolated,
  domain,
  height = 32,
  color,
  className,
  ariaLabel,
}: {
  samples: Sample[];
  extrapolated?: Sample;
  domain?: [number, number];
  height?: number;
  color?: string;
  className?: string;
  ariaLabel?: string;
}) => {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const wrapRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(wrapRef);

  const all = extrapolated ? [...samples, extrapolated] : samples;

  let minT = Infinity;
  let maxT = -Infinity;
  let min = Infinity;
  let max = -Infinity;
  for (const s of all) {
    if (s.t < minT) minT = s.t;
    if (s.t > maxT) maxT = s.t;
    if (s.value < min) min = s.value;
    if (s.value > max) max = s.value;
  }
  if (domain) {
    [minT, maxT] = domain;
  }
  const spanT = Math.max(1e-9, maxT - minT);
  const range = Math.max(1e-9, max - min);
  const displayMax = max + range * 0.1;
  const displayMin = min - range * 0.2;

  const x = (t: number) => ((t - minT) / spanT) * (width - 2);
  const y = (v: number) =>
    ((displayMax - v) / (displayMax - displayMin)) * height;

  const linePts = samples.map((s) => [x(s.t), y(s.value)] as [number, number]);
  const lineD = bumpX(linePts);
  const last = samples[samples.length - 1];
  const tip = extrapolated ?? last;

  return (
    <div
      className={`${styles.wrap}${className ? ` ${className}` : ''}`}
      ref={wrapRef}
      role="img"
      aria-label={ariaLabel ?? 'Readings over time'}
      style={color ? ({ '--color': color } as CSSProperties) : undefined}
    >
      {width > 0 && all.length > 0 && (
        <svg width={width} height={height + TICK_BAND} className={styles.svg}>
          <defs>
            <linearGradient id={`${uid}-fade`} x1="1" y1="0" x2="0" y2="0">
              <stop offset="0%" stopColor="#fff" />
              <stop offset="75%" stopColor="#fff" stopOpacity="0.75" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <linearGradient id={`${uid}-area`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
            <linearGradient id={`${uid}-tick`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
              <stop offset="25%" stopColor="currentColor" stopOpacity="0.5" />
              <stop offset="90%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
            <mask id={`${uid}-mask`}>
              <rect
                width={width}
                height={height + TICK_BAND}
                fill={`url(#${uid}-fade)`}
              />
            </mask>
          </defs>

          <g mask={`url(#${uid}-mask)`}>
            <line
              className={styles.guide}
              x1={0}
              y1={y(max)}
              x2={width}
              y2={y(max)}
              strokeWidth={1}
              strokeDasharray="3"
            />
            <line
              className={styles.guide}
              x1={0}
              y1={y(min)}
              x2={width}
              y2={y(min)}
              strokeWidth={1}
              strokeDasharray="3"
            />

            {lineD && (
              <path
                d={`${lineD} L ${x(last.t)} ${height} L ${linePts[0]?.[0] ?? 0} ${height} Z`}
                fill={`url(#${uid}-area)`}
                fillOpacity={0.14}
                stroke="none"
              />
            )}
            <path
              className={styles.line}
              d={lineD}
              fill="none"
              strokeWidth={1}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {extrapolated && last && (
              <path
                className={styles.line}
                d={bumpX([
                  [x(last.t), y(last.value)],
                  [x(extrapolated.t), y(extrapolated.value)],
                ])}
                fill="none"
                strokeWidth={1}
                strokeDasharray="2 3"
                opacity={0.5}
              />
            )}

            {samples.map((s, i) =>
              i === 0 ? null : (
                <rect
                  key={i}
                  x={x(s.t)}
                  y={y(s.value) + TICK_OFFSET}
                  width={1}
                  height={TICK_LENGTH}
                  rx={0.5}
                  fill={`url(#${uid}-tick)`}
                />
              )
            )}

            <circle
              className={styles.tip}
              cx={x(tip.t)}
              cy={y(tip.value)}
              r={2}
              opacity={extrapolated ? 0.5 : 1}
            />
          </g>
        </svg>
      )}
    </div>
  );
};
